import { useDocumentData } from 'features/libra/document/DocumentDataContext'
import { useDocumentUI } from 'features/libra/document/DocumentUIContext'
import { DocumentNavigationSidebar } from 'features/libra/stepComponents/niiDataHealth/DocumentNavigationSidebar'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { FC, useCallback, useState } from 'react'

interface DocumentNavigationSidebarContainerProps {
  unitId: string
  onUploadComplete?: (documentIds: ReadonlyArray<string>) => void
  showUploader?: boolean
}

/**
 * Container component that connects the generic DocumentNavigationSidebar
 * to the DocumentNavigationContext. This separates concerns and makes
 * the sidebar reusable across different workflows.
 */
export const DocumentNavigationSidebarContainer: FC<
  DocumentNavigationSidebarContainerProps
> = ({ unitId, onUploadComplete, showUploader = false }) => {
  // Separate data and UI concerns
  const {
    metricsByCategory,
    documentsByCategory,
    isLoading,
    forceRefresh,
    requiredCategories,
  } = useDocumentData()

  const { selectedCategory, setSelectedCategory } = useDocumentUI()

  // Local-only categories that exist until a file is dropped into them
  const [localCategories, setLocalCategories] = useState<string[]>([])

  const handleAddLocalCategory = useCallback((category: string) => {
    setLocalCategories((prev) =>
      prev.includes(category) ? prev : [...prev, category]
    )
  }, [])

  const handleRemoveLocalCategory = useCallback((category: string) => {
    setLocalCategories((prev) => prev.filter((c) => c !== category))
  }, [])

  // Combine all categories (required + those with documents + local temp ones)
  const allCategories = Array.from(
    new Set([
      ...requiredCategories,
      ...metricsByCategory.keys(),
      ...localCategories,
    ])
  )

  // Derive site + org from the ticket tags already in context — no extra query needed
  const { ticketTags, readOnly } = useTicketAccess()
  const siteId = ticketTags.site ?? undefined
  const organizationId = ticketTags.organization ?? undefined

  return (
    <DocumentNavigationSidebar
      unitId={unitId}
      siteId={siteId}
      organizationId={organizationId}
      onUploadComplete={onUploadComplete}
      showUploader={showUploader}
      readOnly={readOnly}
      categories={allCategories}
      requiredCategories={requiredCategories}
      localCategories={localCategories}
      metricsByCategory={metricsByCategory}
      documentsByCategory={documentsByCategory}
      isLoading={isLoading}
      onRefresh={forceRefresh}
      selectedCategory={selectedCategory}
      onCategorySelect={setSelectedCategory}
      onAddLocalCategory={handleAddLocalCategory}
      onRemoveLocalCategory={handleRemoveLocalCategory}
    />
  )
}
